import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Check, X, ChevronRight, Search
} from 'lucide-react';
import { Card, Button } from '@components/ui';
import {
    getPersonnelByRole,
    getRoleLabel,
    getRoleColor,
    personnelRoles
} from '@mocks/personel';
import clsx from 'clsx';

/**
 * PersonelYoklama — Yoklama kartları bileşeni
 * Sahada telefondan hızlı yoklama almak için tasarlandı.
 */
export default function PersonelYoklama({
    attendance,
    setAttendance,
    hasChanges,
    setHasChanges,
    onSaveAttendance,
    allPersonnel
}) {
    const navigate = useNavigate();
    const [selectedRole, setSelectedRole] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');

    const personnel = useMemo(() => {
        let filtered = getPersonnelByRole(selectedRole);
        if (searchQuery) {
            filtered = filtered.filter(p =>
                p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                getRoleLabel(p.role).toLowerCase().includes(searchQuery.toLowerCase())
            );
        }
        return filtered;
    }, [selectedRole, searchQuery]);

    // Tek tıkla yoklama toggle
    const toggleAttendance = (personId) => {
        setAttendance(prev => ({
            ...prev,
            [personId]: !prev[personId]
        }));
        setHasChanges(true);
    };

    // Tümünü seç/kaldır
    const selectAll = (value) => {
        const newAttendance = { ...attendance };
        personnel.forEach(p => {
            newAttendance[p.id] = value;
        });
        setAttendance(newAttendance);
        setHasChanges(true);
    };

    const presentCount = personnel.filter(p => attendance[p.id]).length;
    const absentCount = personnel.length - presentCount;

    return (
        <>
            {/* Arama */}
            <div className="relative max-w-xs">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
                <input
                    type="text"
                    placeholder="Personel ara..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-bg-tertiary border border-border-light rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                />
            </div>

            {/* Rol Filtresi + Toplu Seçim */}
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex flex-wrap items-center gap-2">
                    <button
                        onClick={() => setSelectedRole('all')}
                        className={clsx(
                            "px-3 py-1.5 text-sm rounded-lg transition-colors",
                            selectedRole === 'all'
                                ? "bg-primary text-white"
                                : "bg-bg-tertiary text-text-secondary hover:bg-bg-hover"
                        )}
                    >
                        Tümü ({allPersonnel.length})
                    </button>
                    {personnelRoles.map((role) => {
                        const count = allPersonnel.filter(p => p.role === role.key).length;
                        return (
                            <button
                                key={role.key}
                                onClick={() => setSelectedRole(role.key)}
                                className={clsx(
                                    "px-3 py-1.5 text-sm rounded-lg transition-colors",
                                    selectedRole === role.key
                                        ? "bg-primary text-white"
                                        : "bg-bg-tertiary text-text-secondary hover:bg-bg-hover"
                                )}
                            >
                                {role.label} ({count})
                            </button>
                        );
                    })}
                </div>

                {/* Toplu Seçim */}
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => selectAll(true)}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm bg-success/10 text-success rounded-lg hover:bg-success/20 transition-colors"
                    >
                        <Check size={14} /> Tümü Geldi
                    </button>
                    <button
                        onClick={() => selectAll(false)}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm bg-error/10 text-error rounded-lg hover:bg-error/20 transition-colors"
                    >
                        <X size={14} /> Tümü Gelmedi
                    </button>
                </div>
            </div>

            {/* Personel Listesi - Kart Görünümü */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {personnel.map((person) => {
                    const isPresent = attendance[person.id] ?? false;
                    return (
                        <Card
                            key={person.id}
                            padding="none"
                            className={clsx(
                                "group overflow-hidden transition-all cursor-pointer hover:shadow-lg",
                                isPresent ? "ring-2 ring-success" : "ring-1 ring-border-light"
                            )}
                        >
                            {/* Yoklama Toggle - Büyük Tıklanabilir Alan */}
                            <div
                                onClick={() => toggleAttendance(person.id)}
                                className={clsx(
                                    "p-4 flex items-center justify-between transition-colors",
                                    isPresent ? "bg-success/10" : "bg-bg-secondary hover:bg-bg-hover"
                                )}
                            >
                                <div className="flex items-center gap-3 min-w-0">
                                    <div className={clsx(
                                        "w-12 h-12 rounded-full flex items-center justify-center shrink-0 text-lg font-bold",
                                        isPresent ? "bg-success text-white" : "bg-bg-tertiary text-text-muted"
                                    )}>
                                        {isPresent ? <Check size={24} /> : person.name.charAt(0)}
                                    </div>
                                    <div className="min-w-0">
                                        <h3 className="font-semibold text-text-primary truncate">
                                            {person.name}
                                        </h3>
                                        <span className={clsx(
                                            "inline-flex px-2 py-0.5 rounded-full text-xs font-medium text-white",
                                            getRoleColor(person.role)
                                        )}>
                                            {getRoleLabel(person.role)}
                                        </span>
                                    </div>
                                </div>
                                <div className={clsx(
                                    "text-sm font-medium",
                                    isPresent ? "text-success" : "text-text-muted"
                                )}>
                                    {isPresent ? "Geldi" : "Gelmedi"}
                                </div>
                            </div>

                            {/* Kart Alt Bilgi */}
                            <div className="p-3 bg-bg-tertiary border-t border-border-light flex items-center justify-between">
                                <div className="text-sm text-text-secondary">
                                    ₺{person.dailyWage.toLocaleString('tr-TR')}/gün
                                </div>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`/insaat/personel/${person.id}`);
                                    }}
                                    className="flex items-center gap-1 text-sm text-primary hover:underline"
                                >
                                    Detay <ChevronRight size={14} />
                                </button>
                            </div>
                        </Card>
                    );
                })}
            </div>

            {personnel.length === 0 && (
                <Card className="text-center py-12">
                    <Search size={48} className="mx-auto text-text-muted mb-4" />
                    <p className="text-text-secondary">
                        {searchQuery ? 'Arama sonucu bulunamadı' : 'Henüz personel eklenmemiş'}
                    </p>
                </Card>
            )}
        </>
    );
}
